import { Clock, IndianRupee } from "lucide-react";
import type * as React from "react";
import { useTranslation } from "react-i18next";
import type { Proposal } from "@/features/projects/services/projects-api.service";
import { formatRupees } from "@/lib/format";

interface ProposalProps {
	proposal: Proposal;
}

const BREAKDOWN_LINES = [
	{ key: "designFees", labelKey: "proposals.costBreakdownDesignFees" },
	{ key: "labor", labelKey: "proposals.costBreakdownLabor" },
	{ key: "materials", labelKey: "proposals.costBreakdownMaterials" },
	{ key: "other", labelKey: "proposals.costBreakdownOther" },
] as const;

function TextBlock({
	label,
	text,
}: {
	label: string;
	text: string;
}): React.ReactElement {
	return (
		<div className="rounded-lg bg-dashboard-surface p-3">
			<p className="mb-1 text-xs font-medium text-dashboard-muted">{label}</p>
			<p className="text-sm text-dashboard-text">{text}</p>
		</div>
	);
}

/** The cost and timeline a designer quoted. */
export function ProposalSummary({
	proposal,
}: ProposalProps): React.ReactElement {
	const { t } = useTranslation();
	return (
		<div className="grid gap-4 sm:grid-cols-2">
			<div className="flex items-center gap-2">
				<IndianRupee className="h-4 w-4 text-dashboard-muted" />
				<div>
					<p className="text-xs text-dashboard-muted">
						{t("proposals.costEstimate")}
					</p>
					<p className="font-semibold text-dashboard-text">
						{formatRupees(proposal.costEstimate)}
					</p>
				</div>
			</div>
			<div className="flex items-center gap-2">
				<Clock className="h-4 w-4 text-dashboard-muted" />
				<div>
					<p className="text-xs text-dashboard-muted">
						{t("project.timeline")}
					</p>
					<p className="font-semibold text-dashboard-text">
						{proposal.timelineWeeks}{" "}
						{proposal.timelineWeeks === 1
							? t("common.week")
							: t("common.weeks")}
					</p>
				</div>
			</div>
		</div>
	);
}

/** Everything else a proposal carries; sections it leaves empty are omitted. */
export function ProposalDetail({
	proposal,
}: ProposalProps): React.ReactElement {
	const { t } = useTranslation();
	const breakdown = BREAKDOWN_LINES.flatMap((line) => {
		const amount = proposal.costBreakdown?.[line.key];
		return amount != null && amount > 0 ? [{ ...line, amount }] : [];
	});

	return (
		<div className="space-y-4">
			{proposal.scopeDescription && (
				<TextBlock
					label={t("proposals.scopeDescription")}
					text={proposal.scopeDescription}
				/>
			)}
			{proposal.approach && (
				<TextBlock label={t("proposals.approach")} text={proposal.approach} />
			)}
			{breakdown.length > 0 && (
				<div>
					<p className="mb-2 text-xs font-medium text-dashboard-muted">
						{t("proposals.costBreakdownLabel")}
					</p>
					<div className="grid gap-2 sm:grid-cols-2">
						{breakdown.map((line) => (
							<div
								key={line.key}
								className="flex items-center justify-between rounded-md bg-dashboard-surface px-3 py-2"
							>
								<span className="text-xs text-dashboard-muted">
									{t(line.labelKey)}
								</span>
								<span className="text-sm font-medium text-dashboard-text">
									{formatRupees(line.amount)}
								</span>
							</div>
						))}
					</div>
				</div>
			)}
			{proposal.notes && (
				<TextBlock label={t("proposals.notes")} text={proposal.notes} />
			)}
		</div>
	);
}
